import { Empty, Tag } from "antd";
import PropTypes from "prop-types";
import { useRef } from "react";

const STATUS_CONFIG = {
    success: { color: "success", labelKey: "payment.success" },
    failed: { color: "error", labelKey: "payment.failed" },
    pending: { color: "warning", labelKey: "payment.pending" },
};

const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return "—";
        return d.toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "—";
    }
};

const formatDisplayValue = (value, t) => {
    if (value === null || value === undefined) return t("notAvailable");
    const trimmed = String(value).trim();
    return trimmed || t("notAvailable");
};

const StatusTag = ({ status, t }) => {
    const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.pending;
    return <Tag color={config.color}>{t(config.labelKey)}</Tag>;
};

StatusTag.propTypes = {
    status: PropTypes.string,
    t: PropTypes.func.isRequired,
};

const PaymentEventsTable = ({ events, t }) => {
    const scrollRef = useRef(null);
    const paymentEvents = [...events].reverse();

    const handleWheel = (event) => {
        const container = scrollRef.current;
        if (!container) return;

        const canScrollHorizontally = container.scrollWidth > container.clientWidth;
        if (!canScrollHorizontally) return;

        event.preventDefault();
        container.scrollLeft += event.deltaY;
    };

    if (!paymentEvents.length) {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t("payment.noPaymentEvents")}
                className="py-8"
            />
        );
    }

    return (
        <div className="min-w-0">
            <div
                ref={scrollRef}
                className="payment-events-scroll overflow-hidden rounded-xl border border-[#f0f0f0]"
                onWheel={handleWheel}>
                <table className="sc-table fontRegular payment-events-table">
                    <thead>
                        <tr className="border-b border-[#f0f0f0] bg-[#FAFAFC]">
                            <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-medium text-[#7f8c8d]">
                                {t("payment.amount")}
                            </th>
                            <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-medium text-[#7f8c8d]">
                                {t("payment.status")}
                            </th>
                            <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-medium text-[#7f8c8d]">
                                {t("payment.reservationProcessType")}
                            </th>
                            <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-medium text-[#7f8c8d]">
                                {t("payment.type")}
                            </th>
                            <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-medium text-[#7f8c8d]">
                                {t("payment.paymentPlatform")}
                            </th>
                            <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-medium text-[#7f8c8d]">
                                {t("payment.paymentIntentId")}
                            </th>
                            <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-medium text-[#7f8c8d]">
                                {t("payment.refundId")}
                            </th>
                            <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-medium text-[#7f8c8d]">
                                {t("payment.updatedDate")}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {paymentEvents.map((event) => (
                            <tr
                                key={event.id}
                                className="border-b border-[#f0f0f0] last:border-b-0 hover:bg-[#FAFAFC]/80">
                                <td
                                    className={`whitespace-nowrap px-4 py-3 text-right text-sm font-medium ${
                                        event.eventType === "refund" ? "text-[#d48806]" : "text-[#2F2F46]"
                                    }`}>
                                    {event.eventType === "refund" ? "-" : ""}$
                                    {Number(event.amount).toFixed(2)}
                                </td>
                                <td className="whitespace-nowrap px-4 py-3">
                                    <StatusTag status={event.status} t={t} />
                                </td>
                                <td className="whitespace-nowrap px-4 py-3 text-sm capitalize text-[#667085]">
                                    {event.reservationProcess || "—"}
                                </td>
                                <td className="whitespace-nowrap px-4 py-3 text-sm capitalize text-[#667085]">
                                    {event.eventType}
                                </td>
                                <td className="whitespace-nowrap px-4 py-3 text-sm capitalize text-[#667085]">
                                    {formatDisplayValue(event.paymentPlatform, t)}
                                </td>
                                <td
                                    className="whitespace-nowrap px-4 py-3 text-sm text-[#667085]"
                                    title={formatDisplayValue(event.paymentIntentId, t)}>
                                    {formatDisplayValue(event.paymentIntentId, t)}
                                </td>
                                <td
                                    className="whitespace-nowrap px-4 py-3 text-sm text-[#667085]"
                                    title={formatDisplayValue(
                                        event.eventType === "refund" ? event.refundId : null,
                                        t,
                                    )}>
                                    {formatDisplayValue(
                                        event.eventType === "refund" ? event.refundId : null,
                                        t,
                                    )}
                                </td>
                                <td className="whitespace-nowrap px-4 py-3 text-sm text-[#667085]">
                                    {formatDate(event.updatedAt)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p className="fontRegular mt-1 text-right text-[11px] text-[#9b9ba5]">{t("payment.scrollForMore")}</p>
        </div>
    );
};

PaymentEventsTable.propTypes = {
    events: PropTypes.arrayOf(PropTypes.object),
    t: PropTypes.func.isRequired,
};

PaymentEventsTable.defaultProps = {
    events: [],
};

export default PaymentEventsTable;
